import React, { FC } from 'react'
import styled from 'styled-components'

import { WidthProps } from 'types/styled'

import Icon from '../Icon'
import IconButton from '../Buttons/IconButton'
import Text from '../Text'

import { TitleProps, WindowProps } from './types'
import Flex from 'components/Flex'

type Params = {
  type?: string
  isReadOnly: boolean
  isMinimized: boolean
  subTitle: string
}
const isTitleDisplay = ({ type, isReadOnly, isMinimized, subTitle }: Params) => {
  if (type === 'appointment') {
    return !isReadOnly && !(isMinimized && (isReadOnly || subTitle))
  }
  return type !== 'appointment'
}

export const Title: FC<TitleProps> = ({
  canClose = true,
  customComponent,
  icon = ['fal', 'pencil'],
  readOnlyIcon,
  type,
  isMinimized,
  isReadOnly = false,
  minimizeIcon = 'minus',
  onClose,
  onMinimize,
  restoreIcon,
  title,
  subTitle = '',
  width,
  isPriority = false,
}) => {
  const isTitleVisible = isTitleDisplay({ type, isReadOnly, isMinimized, subTitle })
  return (
    <TitleBar isMinimized={isMinimized} isReadOnly={isReadOnly} width={width}>
      <TitleSectionWrapper>
        {!isReadOnly && icon && <Icon color="white" fontSize={15} icon={icon} mr={10} />}
        {isReadOnly && readOnlyIcon && <Icon color="grey7" fontSize={15} icon={readOnlyIcon} mr={10} />}
        {isTitleVisible && (
          <Text
            color={isReadOnly ? 'gray7' : 'white'}
            fontSize={14}
            fontWeight="medium"
            lineHeight="24px"
            textTransform="capitalize"
            display="inline-block"
          >
            {title}
          </Text>
        )}
        {subTitle && (
          <Flex justifyContent="flex-start" alignItems="center" gap="4px">
            {isTitleVisible && (
              <Text fontSize={14} fontWeight="medium" color={isReadOnly ? 'gray7' : 'white'} display="inline">
                -
              </Text>
            )}
            <Text
              display="inline"
              ellipsis
              maxLength={75}
              color={isReadOnly ? 'gray7' : 'white'}
              fontSize={14}
              fontWeight="medium"
              lineHeight="24px"
              textAlign="left"
              width={isMinimized ? '120px' : 'auto'}
            >
              {subTitle}
            </Text>
          </Flex>
        )}
      </TitleSectionWrapper>
      <ButtonsWrapper>
        {!isMinimized && customComponent}
        {isMinimized && restoreIcon && <IconButton icon={restoreIcon} mx={12} size="small" variant="ghost" />}
        {!isPriority && !isMinimized && (
          <IconButton icon={minimizeIcon} mx={12} onClick={onMinimize} size="small" variant="ghost" />
        )}
        {!isPriority && canClose && onClose && (
          <IconButton icon={['far', 'times']} onClick={onClose} size="small" variant="ghost" />
        )}
      </ButtonsWrapper>
    </TitleBar>
  )
}

const Window = <D extends string>({
  children,
  isPadded = true,
  isPriority = false,
  width = 700,
  ...props
}: WindowProps<D>) => (
  <Container width={props.isMinimized ? undefined : width}>
    <Title {...props} width={width} isReadOnly={props.windowState !== 'active'} isPriority={isPriority} />
    {children && (
      <Body isPadded={isPadded} isMinimized={props.isMinimized}>
        {React.cloneElement(children, {
          formState: props.windowState,
          onClose: props.onClose,
          isPriority: isPriority,
        })}
      </Body>
    )}
  </Container>
)

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  svg {
    color: ${(p) => p.theme.colors.gray5};
  }
`

const Container = styled.div<{ width?: WidthProps }>`
  background-color: ${(p) => p.theme.colors.white};
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  width: ${(p) => (p.width ? `${p.width}px` : '100%')};
  border-radius: 4px 4px 0 0;
`

const Body = styled.div<{ isPadded: boolean; isMinimized?: boolean }>`
  ${(p) => `
    padding: ${p.isPadded ? '8px 16px' : '0'};
    visibility: ${p.isMinimized ? 'hidden' : 'visible'};
  `}
  max-height: 90vh;
  overflow-y: overlay;
`

const TitleBar = styled.div<{ isMinimized: boolean; isReadOnly: boolean; width?: WidthProps }>`
  align-items: center;
  background-color: ${(p) => (p.isReadOnly ? p.theme.colors.gray3 : p.theme.colors.gray7)};
  border-radius: 4px 4px 0 0;
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;
  user-select: none;
  cursor: ${(p) => (p.isMinimized ? 'pointer' : 'default')};
  width: ${(p) =>
    p.isMinimized
      ? '200px'
      : p.width
      ? `${typeof p.width === 'number' ? `calc(${p.width} - 34)` : p.width}px`
      : '100%'};
`

const TitleSectionWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  padding-left: 10px;
  width: 100%;
`

export default Window
